/**
 * Safe localStorage / sessionStorage helpers.
 *
 * The naive pattern `JSON.parse(localStorage.getItem('x') || 'null')` looks
 * defensive but breaks when the stored value is the literal string
 * "undefined" — which happens whenever code somewhere does
 * `localStorage.setItem('x', undefined)` and JavaScript coerces the
 * undefined to a string.
 *
 * `JSON.parse("undefined")` then throws "undefined is not valid JSON",
 * which crashes the whole React tree via ErrorBoundary.
 *
 * These helpers handle every junk value we've seen in the wild: null,
 * the string "undefined", "null", invalid JSON, and access errors in
 * private mode / blocked storage.
 */

const BAD_VALUES = new Set([null, undefined, '', 'undefined', 'null']);

export function safeGetJSON(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);
    if (BAD_VALUES.has(raw)) return fallback;
    return JSON.parse(raw);
  } catch (err) {
    // Corrupt JSON — clean it up so the next read works.
    try { localStorage.removeItem(key); } catch (cleanupErr) {
      // eslint-disable-next-line no-console
      console.warn(`safeStorage: cleanup of ${key} failed`, cleanupErr);
    }
    // eslint-disable-next-line no-console
    console.warn(`safeStorage: corrupt JSON at "${key}" — returning fallback`, err);
    return fallback;
  }
}

export function safeSetJSON(key, value) {
  try {
    if (value === undefined) {
      // setItem(_, undefined) coerces to "undefined" — never store that.
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    // Private mode / storage quota / blocked — non-fatal but worth knowing.
    // eslint-disable-next-line no-console
    console.warn(`safeStorage: write of "${key}" failed`, err);
  }
}

/**
 * Run once on app boot. Sweeps localStorage and removes any entries whose
 * value is the literal string "undefined" — these are guaranteed to crash
 * any consumer that `JSON.parse`s them.
 */
export function purgeUndefinedLocalStorage() {
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && localStorage.getItem(k) === 'undefined') toRemove.push(k);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
    if (toRemove.length) {
      // eslint-disable-next-line no-console
      console.warn(`Cleaned ${toRemove.length} corrupt localStorage entries:`, toRemove);
    }
  } catch {
    /* ignore */
  }
}
